import { UserGroupMemberRecordListPartner, UserRole } from '@navio/nsdk';
import { invalidateQuery, invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon } from 'noddi-ui-common';
import {
  ApiErrorMessageInternal,
  LoadingScreen,
  NavioInfoCard,
  NoddiAsyncConfirmationDialogButton,
  NoddiBadge,
  NoddiButton,
  NoddiCard,
  useAppForm
} from 'noddi-ui-web';
import { getPersonalAccountPurposeLabel } from 'noddi-util';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { getLongAddressName } from '../../utils/address';
import { getLanguagePropertiesFromCountryCode } from '../../utils/languages';
import { getBrandRelationshipItem } from './getBrandRelationshipItem';
import { UserGroupTypeBadge } from './UserGroupTypeBadge';

const validationSchema = z.object({
  name: z.string().min(1, 'Required')
});

type FormValues = z.input<typeof validationSchema>;

export const DetailsTab = ({
  personalMember,
  onDeleteSuccess
}: {
  personalMember?: UserGroupMemberRecordListPartner;
  onDeleteSuccess?: () => void;
}) => {
  const { id: userGroupIdString } = useParams();
  const userGroupId = Number(userGroupIdString);
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const {
    data: userGroup,
    error: userGroupError,
    isPending: isUserGroupPending
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroup,
    input: { userGroupId }
  });

  const {
    data: user,
    error: userError,
    isLoading: isUserLoading
  } = noddiAsync.useGet({
    type: URLKeys.getAccount,
    input: { userId: personalMember?.user.id as number },
    queryConfig: { enabled: !!personalMember }
  });

  const {
    isPending: userGroupAddressesIsPending,
    data: userGroupAddresses,
    error: userGroupAddressesError
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupAddresses,
    input: { userGroupId }
  });

  const { mutateAsync: patchUserGroup } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroup,
    queryConfig: {
      onSuccess: async () => {
        invalidateQueryExactMatch({ urlKey: URLKeys.getUserGroup, input: { userGroupId } });
      }
    }
  });

  const { mutateAsync: deleteUserGroup, isPending: isDeletePending } = noddiAsync.useDelete({
    type: URLKeys.deleteUserGroup,
    queryConfig: {
      onSuccess: async () => {
        onDeleteSuccess?.();
        invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroups });
      }
    }
  });

  if (isUserGroupPending || isUserLoading || userGroupAddressesIsPending) {
    return <LoadingScreen />;
  }

  if (userGroupError || userError || userGroupAddressesError) {
    return <ApiErrorMessageInternal error={[userGroupError, userError, userGroupAddressesError]} />;
  }

  const isPersonal = userGroup.type.value === 'personal';
  const personalAccountPurposeLabel = getPersonalAccountPurposeLabel(userGroup.personalAccountPurpose);

  return (
    <div className='flex flex-col gap-4'>
      <div className={cn(user && 'grid grid-cols-1 gap-5 lg:grid-cols-2')}>
        {user && (
          <NavioInfoCard
            title='Personal information'
            items={[
              { label: 'First name', value: user.firstName },
              { label: 'Last name', value: user.lastName },
              { label: 'Email', value: user.email, href: user.email ? `mailto:${user.email}` : undefined },
              {
                label: 'Phone number',
                value: user.phoneNumber,
                type: 'phone',
                href: user.phoneNumber ? `tel:${user.phoneNumber}` : undefined
              }
            ]}
          />
        )}
        <NavioInfoCard
          title='Account information'
          items={[
            { label: 'Account type', value: <UserGroupTypeBadge userGroup={userGroup} /> },
            !!personalAccountPurposeLabel && {
              label: 'Personal account',
              value: <NoddiBadge variant='outline'>{personalAccountPurposeLabel}</NoddiBadge>
            },
            !!userGroup.brandRelationship && getBrandRelationshipItem(userGroup.brandRelationship),
            { label: 'Created', value: userGroup.createdAt, type: 'date' },
            { label: 'Last updated', value: userGroup.updatedAt, type: 'date' },
            !!user && { label: 'Last login', value: user.lastLogin, type: 'date' },
            !!user && { label: 'Language', value: getLanguagePropertiesFromCountryCode(user.languageCode).name }
          ]}
        />
      </div>

      <div className={cn(user && 'grid grid-cols-1 gap-5 lg:grid-cols-2')}>
        {user && (
          <NavioInfoCard
            title='Permissions'
            items={[
              {
                label: 'Administrator',
                value: user.roles.some((r) => r.value === UserRole.SUPERUSER),
                type: 'boolean'
              },
              { label: 'Staff member', value: user.roles.some((r) => r.value === UserRole.STAFF), type: 'boolean' },
              { label: 'Manager', value: user.roles.some((r) => r.value === UserRole.MANAGER), type: 'boolean' },
              { label: 'Worker', value: user.roles.some((r) => r.value === UserRole.WORKER), type: 'boolean' }
            ]}
          />
        )}
        {user && personalMember && (
          <NavioInfoCard
            title='Notifications'
            items={[
              {
                label: 'Email booking notifications',
                value: personalMember.emailBookingNotifications,
                type: 'boolean'
              },
              { label: 'Email calendar invites', value: personalMember.emailCalendarInvites, type: 'boolean' },
              { label: 'Email newsletter', value: personalMember.emailNewsletter, type: 'boolean' },
              { label: 'SMS booking notifications', value: personalMember.smsBookingNotifications, type: 'boolean' },
              { label: 'SMS marketing', value: personalMember.smsMarketing, type: 'boolean' }
            ]}
          />
        )}
      </div>

      {userGroupAddresses.results.length > 0 && (
        <NoddiCard title='Saved addresses' contentClassName='flex flex-col gap-3'>
          {(showAllAddresses ? userGroupAddresses.results : userGroupAddresses.results.slice(0, 5)).map(
            ({ address }) => (
              <div key={address.id} className='flex items-baseline gap-2'>
                <NoddiIcon name='MapPin' />
                <p>{getLongAddressName(address)}</p>
              </div>
            )
          )}
          {userGroupAddresses.results.length > 5 && (
            <NoddiButton
              variant='outline'
              className='mt-2 w-fit'
              onClick={() => setShowAllAddresses(!showAllAddresses)}
            >
              {showAllAddresses ? 'View less' : `View ${userGroupAddresses.results.length - 5} more`}
            </NoddiButton>
          )}
        </NoddiCard>
      )}

      {!isPersonal && (
        // It does not make sense to edit the name of a personal user group, this is tied to the user's name.
        // If they want to change it, they should change their personal name, they do it on user model.
        <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
          <EditUserGroupNameForm
            name={userGroup.name}
            onSubmit={(values) =>
              patchUserGroup({
                userGroupId: userGroup.id,
                name: values.name
              })
            }
          />
          <NoddiCard title='Delete customer' description='This is only available for non-personal customers.'>
            <NoddiAsyncConfirmationDialogButton
              variant='destructive'
              confirmTitle='Delete customer'
              confirmDescription='Are you sure you want to delete this customer? This action cannot be undone and will permanently remove all associated data.'
              confirmText='Delete'
              loading={isDeletePending}
              onConfirm={async () => {
                await deleteUserGroup({ userGroupId });
              }}
            >
              Delete customer
            </NoddiAsyncConfirmationDialogButton>
          </NoddiCard>
        </div>
      )}
    </div>
  );
};

type EditUserGroupNameFormProps = {
  name: string;
  onSubmit: (values: FormValues) => Promise<unknown>;
};

const EditUserGroupNameForm = ({ name, onSubmit }: EditUserGroupNameFormProps) => {
  const defaultValues: FormValues = { name };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    }
  });

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <NoddiCard title='Edit customer' description='This is only available for non-personal customers.'>
          <form.AppField name='name'>{(field) => <field.Input label='Group name' />}</form.AppField>
          <form.SubscribeButton className='mt-2' label='Update' />
        </NoddiCard>
      </form>
    </form.AppForm>
  );
};
